/**
 * Where the x axis puts its marks, in the **point's** time zone.
 *
 * Not `map/layers/ticks.ts`. That module marks a frame slider, whose track is
 * index-spaced — the positions are frame indices and the gap between two
 * frames is not the same everywhere. An axis is time-spaced, so the same
 * boundary lands somewhere else. What the two share is the round-interval
 * table and the day-boundary emphasis, and those live in `lib/time.ts` so
 * there is one copy of them.
 *
 * The zone is the point's because a day boundary is the axis's most useful
 * cue, and a diurnal curve marked against the viewer's midnight puts night in
 * the wrong place. Every other panel keeps the viewer's zone; the chart says
 * which zone it is drawing in when the two differ.
 */

import { asZoneLocal, chooseInterval, DAY, formatInZone, MINUTE, zoneOffsetMinutes } from '../time'

export interface TimeTick {
  at: Date
  /** Local midnight. Drawn stronger and labelled with the date. */
  day: boolean
  label: string
}

/**
 * About `target` marks between `from` and `to`.
 *
 * The caller passes the number because the module cannot see pixels — the same
 * arrangement `TimeSlider` already has, where a desktop track asks for 24 and a
 * phone's asks for 8. A 300 px chart gets 6 and a 430 px one gets 10.
 */
export function timeTicks(
  from: Date,
  to: Date,
  target: number,
  tz: string | null | undefined,
): TimeTick[] {
  const start = from.getTime()
  const end = to.getTime()
  if (!Number.isFinite(start) || !Number.isFinite(end) || end <= start) return []

  const interval = chooseInterval(end - start, Math.max(1, target))
  const out: TimeTick[] = []

  // Walk in local terms and re-read the offset at every step, because it moves
  // at a DST boundary — which is exactly the day somebody looks hardest at a
  // temperature curve, and exactly the bug a test written in July never sees.
  let cursor = floorLocal(start, interval, tz)
  if (cursor < start) cursor += interval
  for (let i = 0; cursor <= end && i < 500; i++) {
    const at = new Date(cursor)
    const day = asZoneLocal(at, tz) % DAY === 0
    out.push({ at, day, label: labelFor(at, tz, interval, day) })
    cursor = nextLocal(cursor, interval, tz)
  }
  return out
}

/**
 * The instant at which the zone's clock reads `wall` (local ms as UTC).
 *
 * The offset is re-read *at the answer*, not at `near`: across a DST change
 * the two differ, and using `near`'s puts the mark an hour off — after the
 * fall change, "midnight" came out as 1 AM daylight time. `near` only picks
 * between the two readings of a repeated hour, so a cursor in 1 AM CST stays
 * in it. A wall time inside the spring gap (2:30 AM) has no instant; no
 * caller asks for one, because a round boundary is floored from a time that
 * exists and nothing round lies in the gap but 2 AM itself.
 */
function wallToInstant(wall: number, near: number, tz: string | null | undefined): number {
  const guess = wall - zoneOffsetMinutes(new Date(near), tz) * MINUTE
  return wall - zoneOffsetMinutes(new Date(guess), tz) * MINUTE
}

function floorLocal(ms: number, interval: number, tz: string | null | undefined): number {
  const wall = Math.floor(asZoneLocal(new Date(ms), tz) / interval) * interval
  return wallToInstant(wall, ms, tz)
}

function nextLocal(ms: number, interval: number, tz: string | null | undefined): number {
  // Step in elapsed time and re-floor, so a DST shift lands back on a round
  // local time rather than drifting the rest of the axis by an hour. On an
  // hourly or finer axis this repeats 1 AM in the fall, which is the truth:
  // the hour happens twice.
  const snapped = floorLocal(ms + interval, interval, tz)
  if (snapped > ms) return snapped
  // A coarser step that crossed the fall change floors back to where it
  // started (midnight + 3 h of elapsed time is 2 AM standard, which floors to
  // midnight). Step on the wall clock instead: the next mark is 3 AM.
  return wallToInstant(asZoneLocal(new Date(ms), tz) + interval, ms + interval, tz)
}

function labelFor(at: Date, tz: string | null | undefined, interval: number, day: boolean): string {
  if (day) return formatInZone(at, tz, { weekday: 'short', day: 'numeric' })
  if (interval >= DAY) return formatInZone(at, tz, { weekday: 'short', day: 'numeric' })
  if (interval >= 60 * MINUTE) return formatInZone(at, tz, { hour: 'numeric' })
  return formatInZone(at, tz, { hour: 'numeric', minute: '2-digit' })
}
